import { useLocation, useParams } from 'react-router-dom'
import type { AppRedirectProps } from 'lib/routing/AppRedirect'
import { AppRedirect } from 'lib/routing/AppRedirect'
import { useSsrRoutingContext } from 'lib/routing/ssrRoutingContext'
import { interpolateRedirectPath } from 'lib/routing/interpolateRedirectPath'

interface Props extends Omit<AppRedirectProps, 'to'> {
  code?: number
  to: string
  withSearchAndHash?: boolean
}

function getRedirectTo(
  to: string,
  location: ReturnType<typeof useLocation>,
  withSearchAndHash: boolean,
): string {
  // When false, the caller has already included search and hash in `to`; use it as-is.
  if (!withSearchAndHash) {
    return to
  }

  const parsedTo = parseRedirectToString(to)

  return [
    parsedTo.pathname,
    parsedTo.search || location.search,
    parsedTo.hash || location.hash,
  ].join('')
}

function parseRedirectToString(to: string) {
  const [pathAndSearch, hash = ''] = to.split('#')
  const [pathname, search = ''] = pathAndSearch.split('?')

  return {
    hash: hash ? `#${hash}` : '',
    pathname,
    search: search ? `?${search}` : '',
  }
}

function RedirectWithStatus(props: Props) {
  const { code, withSearchAndHash = true, ...redirectProps } = props
  const location = useLocation()
  const params = useParams()
  const ssrRoutingContext = useSsrRoutingContext()

  const redirectTo = interpolateRedirectPath(redirectProps.to, params)
  const to = getRedirectTo(redirectTo, location, withSearchAndHash)

  if (ssrRoutingContext) {
    ssrRoutingContext.statusCode = code || 302
    ssrRoutingContext.url = to
  }

  return <AppRedirect
    {...redirectProps}
    to={to}
  />
}

export default RedirectWithStatus
